import { type BackTarget } from '@components/BackLink'
import PageHeading from '@components/PageHeading'
import { FillPageColumn, PageColumn } from '@components/Page'
import { PublicShell } from '@components/PublicPage'
import { usePublicOverview } from '@hooks'
import { publicTripPath } from '@lib/publicLinks'
import React from 'react'
import { TopBar } from './PublicSectionLayout.styles'

interface PublicSectionLayoutProps {
	/** The shared trip code; the back arrow and share URLs compose from it. */
	tripCode: string
	/** Section heading, e.g. "Ideas", "Accommodations". */
	title: string
	/** Optional count / context line under the title, e.g. "3 places". */
	subtitle?: string
	/** Where the back arrow leads on a per-item detail page (its section list); the shared overview otherwise. */
	backTo?: BackTarget
	/** A workspace page (the itinerary, its map): the member page's full-width column filling the viewport, the heading kept on the shell. */
	fill?: boolean
	children: React.ReactNode
}

/**
 * Happy-path frame for every public per-section detail page: the shared
 * `PublicShell` chrome (SiteHeader view-only pill + Get Orbit CTA, cream
 * surface, footer caption) around the section title — with the back arrow to
 * the shared overview (or a detail page's section list) on its left, same
 * shape as member mode — and the content. Deliberately minimal —
 * no app Layout, no `CtaBand` (kept for the overview marketing surface only).
 * Loading / unavailable / error branches are rendered by `PublicSectionGate`,
 * not this layout.
 */
const PublicSectionLayout: React.FC<PublicSectionLayoutProps> = ({
	tripCode,
	title,
	subtitle,
	backTo,
	fill = false,
	children,
}) => {
	// Trip name for the arrow's label — already cached from the overview page the
	// visitor clicked through (and fetched here on a direct link).
	const { data: overview } = usePublicOverview(tripCode)
	const back = backTo ?? { path: publicTripPath(tripCode), label: overview?.trip.name ?? 'Trip' }

	const content = (
		<>
			<TopBar $onShell={fill}>
				<PageHeading title={title} subtitle={subtitle} backTo={back} />
			</TopBar>
			{children}
		</>
	)

	return (
		<PublicShell>
			{fill ? <FillPageColumn as="main">{content}</FillPageColumn> : <PageColumn as="main">{content}</PageColumn>}
		</PublicShell>
	)
}

export default PublicSectionLayout
